import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { MonthlyIncome, StatisticsModel } from "../../core/api/statistics/StatisticsModel";
import { 
  DataGrid, 
  GridColDef, 
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";

const IncomeTable: React.FC = () => {
  const [data, setData] = useState<MonthlyIncome[]>([]);
  const [totalIncome, setTotalIncome] = useState<MonthlyIncome | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const incomeData = await StatisticsModel.getMonthlyIncome();
      const total: MonthlyIncome = {
        date: "Total",
        totalPrice: incomeData.reduce((sum, item) => sum + (item.totalPrice || 0), 0),
        totalPvm: incomeData.reduce((sum, item) => sum + (item.totalPvm || 0), 0),
        sum: incomeData.reduce((sum, item) => sum + (item.sum || 0), 0),
      };
      setData(incomeData);
      setTotalIncome(total);
    };

    fetchData().catch(console.error);
  }, []);

  // Prepare rows with IDs for DataGrid
  const rows = totalIncome 
    ? [...data.map((item, index) => ({ ...item, id: index })), { ...totalIncome, id: 'total' }]
    : data.map((item, index) => ({ ...item, id: index }));

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<MonthlyIncome>) => {
        return params.row.date === "Total" ? (
          <Typography fontWeight="bold">Total</Typography>
        ) : (
          <span>{format(parseISO(params.row.date as string), "yyyy-MM")}</span>
        );
      },
    },
    {
      field: "totalPrice",
      headerName: "Kaina",
      flex: 1,
      type: "number",
    },
    {
      field: "totalPvm",
      headerName: "PVM",
      flex: 1,
      type: "number",
    },
    {
      field: "sum",
      headerName: "Suma",
      flex: 1,
      type: "number",
    },
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6">
          Mėnesinės pajamos
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          hideFooter
          getRowClassName={(params) => 
            params.id === 'total' ? 'total-row' : params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : ''
          }
          sx={{
            border: 'none',
            '& .total-row': {
              backgroundColor: '#e0e0e0',
              fontWeight: 'bold',
            },
            '& .even-row': {
              backgroundColor: '#efefef',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#bdbdbd',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: '200px',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-overlay': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            maxHeight: '600px',
          }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                Pajamų statistikos nėra
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default IncomeTable;
